const MS_PER_DAY = 24 * 60 * 60 * 1000;

const formatDMY = (date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;

const getCompoundingMonths = (interestCompounding) => {
  switch (interestCompounding) {
    case "Monthly":
      return 1;

    case "Quarterly":
      return 3;

    case "Half Yearly":
      return 6;

    case "Yearly":
      return 12;

    case "No Compounding":
    default:
      return null;
  }
};

const addMonths = (date, months) => {
  const result = new Date(date);
  const originalDay = result.getDate();

  result.setDate(1);
  result.setMonth(result.getMonth() + months);

  const lastDayOfMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0,
  ).getDate();

  result.setDate(Math.min(originalDay, lastDayOfMonth));

  return result;
};

/*
 * Maps a tenureType (from the MenuItem values) to a step config.
 * "days" steps use plain day arithmetic; "months" steps use
 * addMonths so calendar month-length differences are respected.
 *
 * NOTE: your MenuItem has value="monthly" (typo) for Monthly.
 * I've mapped both "monthly" and "monthly" here so this works
 * either way — but you should fix the typo in the JSX too.
 */
const getPeriodConfig = (tenureType) => {
  switch (tenureType) {
    case "daily":
      return { unit: "days", value: 1 };
    case "weekly":
      return { unit: "days", value: 7 };
    case "ten_daily":
      return { unit: "days", value: 10 };
    case "bi_weekly":
      return { unit: "days", value: 15 };
    case "monthly":
    case "monthly":
      return { unit: "months", value: 1 };
    default:
      return { unit: "days", value: 1 };
  }
};

/*
 * Returns the due date for schedule row `index` (0-based),
 * where index 0 === startDate.
 */
const getDueDateForIndex = (start, config, index) => {
  if (config.unit === "months") {
    return addMonths(start, config.value * index);
  }
  return new Date(start.getTime() + config.value * index * MS_PER_DAY);
};

/*
 * Total number of calendar days spanned by `totalTenure` periods,
 * i.e. days between startDate and the final due date. Used for the
 * Fixed Deposit simple-interest calculation (T in "P x R x T / 100").
 */
const getTotalDays = (start, config, totalTenure) => {
  const maturityDate = getDueDateForIndex(start, config, totalTenure);
  return Math.round((maturityDate.getTime() - start.getTime()) / MS_PER_DAY);
};

/*
 * FIXED DEPOSIT (Simple Interest)
 *
 * Produces the SAME per-period schedule shape/loop as Recurring
 * Deposit (one row per tenure period, opening/closing balance
 * building up the same way, same due-date stepping) so the table
 * looks and behaves identically.
 *
 * The difference is purely in the interest math:
 * - Every row's compoundInterest, interest, and maturityAmount are 0.
 *   No per-row interest is calculated or compounded — that's the
 *   whole point of "no compounding" for a Fixed Deposit.
 * - The actual interest is calculated ONCE, as plain Simple Interest
 *   on the total principal over the full tenure:
 *
 *     Simple Interest = Principal x Rate x Time(years) / 100
 *     Maturity Amount = Principal + Simple Interest
 *
 *   These two numbers are only returned in `summary` (interestEarned,
 *   maturityAmount) — the same place the "Maturity Value" circle on
 *   the page already reads from — not on any individual row.
 */
const generateFixedDepositSchedule = ({
  tenure,
  startDate,
  dailyCollAmt,
  rateOfInterest = 0,
  customerId,
  tenureType,
  collectorId = null,
  accountId = null,
  companyId,
  paymentType = "Cash",
}) => {
  if (!tenure || Number(tenure) <= 0) {
    throw new Error("Invalid tenure");
  }

  if (!dailyCollAmt || Number(dailyCollAmt) <= 0) {
    throw new Error("Invalid deposit amount");
  }

  const schedule = [];

  const totalTenure = Number(tenure);
  const collectionAmount = Number(dailyCollAmt);
  const annualInterestRate = Number(rateOfInterest);

  const start = new Date(startDate);

  if (Number.isNaN(start.getTime())) {
    throw new Error("Invalid start date");
  }

  const periodConfig = getPeriodConfig(tenureType);

  // Balance simply accumulates deposits — no interest ever gets
  // added in, since interest is calculated once at the end.
  let balance = 0;

  for (let i = 0; i < totalTenure; i++) {
    const dueDate = getDueDateForIndex(start, periodConfig, i);

    const nextDueDate =
      i < totalTenure - 1 ? getDueDateForIndex(start, periodConfig, i + 1) : null;

    const nextSchedule = nextDueDate ? nextDueDate.toISOString() : null;

    const periodEndDate = nextDueDate
      ? new Date(nextDueDate.getTime() - MS_PER_DAY)
      : dueDate;

    const openingBalance = balance;
    const closingBalance = openingBalance + collectionAmount;

    balance = closingBalance;

    schedule.push({
      emiNo: i + 1,
      periodLabel: `${formatDMY(dueDate)}-${formatDMY(periodEndDate)}`,
      dueDate: dueDate.toISOString(),
      nextSchedule,
      openingBalance,
      collectionAmount,
      Installment: collectionAmount,
      principal: collectionAmount,
      closingBalance,
      // No compounding / no per-row interest for Fixed Deposit.
      compoundInterest: 0,
      interest: 0,
      maturityAmount: 0,
      collectionStatus: null,
      penaltyApplied: 0,
      paymentType,
      customerId,
      collectorId,
      accountId,
      companyId,
      collectedDate: null,
    });
  }

  const lastRow = schedule.at(-1);

  const principalAmount = collectionAmount * totalTenure;

  const totalDays = getTotalDays(start, periodConfig, totalTenure);
  const tenureYears = totalDays / 365;

  /*
   * Simple Interest = P x R x T / 100 — calculated once, on the
   * full principal, over the full tenure.
   */
  const rawInterest = (principalAmount * annualInterestRate * tenureYears) / 100;
  const interestEarned = Math.ceil(rawInterest * 100) / 100;

  const maturityAmount = principalAmount + interestEarned;

  return {
    schedule,
    summary: {
      principalAmount,
      interestEarned,
      maturityAmount,
      maturityDate: formatDMY(new Date(lastRow.dueDate)),
      interestCompounding: "No Compounding",
    },
  };
};

export function generateSavingsSchedule({
  savingMethod = "recurring",
  tenure,
  startDate,
  dailyCollAmt,
  rateOfInterest = 0,
  interestCompounding = "Monthly",
  customerId,
  tenureType,
  collectorId = null,
  accountId = null,
  companyId,
  paymentType = "Cash",
}) {
  // ---- Fixed Deposit: simple interest, no per-row compounding ----
  if (savingMethod === "fixed") {
    return generateFixedDepositSchedule({
      tenure,
      startDate,
      dailyCollAmt,
      rateOfInterest,
      customerId,
      tenureType,
      collectorId,
      accountId,
      companyId,
      paymentType,
    });
  }

  if (!tenure || Number(tenure) <= 0) {
    throw new Error("Invalid tenure");
  }

  if (!dailyCollAmt || Number(dailyCollAmt) <= 0) {
    throw new Error("Invalid collection amount");
  }

  const schedule = [];

  const totalTenure = Number(tenure);
  const collectionAmount = Number(dailyCollAmt);
  const annualInterestRate = Number(rateOfInterest);

  const start = new Date(startDate);

  if (Number.isNaN(start.getTime())) {
    throw new Error("Invalid start date");
  }

  const periodConfig = getPeriodConfig(tenureType);
  const compoundingMonths = getCompoundingMonths(interestCompounding);

  // Balance contains:
  // Deposits + interest that has already been compounded.
  let balance = 0;

  // Interest accumulated since the last compounding date.
  let accruedInterest = 0;

  // Total interest earned throughout the complete schedule.
  let totalInterestEarned = 0;

  // Used for PDF "Comp. Interest" column.
  let compoundInterest = 0;

  // First compounding date.
  //
  // Example:
  // Start Date: 16/07/2026
  // Monthly Compounding Date: 15/08/2026
  //
  // Quarterly:
  // 15/10/2026
  //
  // Half Yearly:
  // 15/01/2027
  //
  // Yearly:
  // 15/07/2027
  let nextCompoundingDate = null;

  if (compoundingMonths) {
    nextCompoundingDate = addMonths(start, compoundingMonths);
    nextCompoundingDate.setDate(nextCompoundingDate.getDate() - 1);
  }

  for (let i = 0; i < totalTenure; i++) {
    const dueDate = getDueDateForIndex(start, periodConfig, i);

    const nextDueDate =
      i < totalTenure - 1 ? getDueDateForIndex(start, periodConfig, i + 1) : null;

    const nextSchedule = nextDueDate ? nextDueDate.toISOString() : null;

    /*
     * Number of days this row's interest should cover.
     *
     * - Normally: gap to the next due date (forward-looking, same
     *   as before where daily rows implicitly covered 1 day).
     * - Last row: no "next" due date to measure against, so fall
     *   back to the nominal period length (config.value for
     *   day-based tenures, or actual days-in-month for monthly).
     */
    const periodDays = nextDueDate
      ? Math.round((nextDueDate.getTime() - dueDate.getTime()) / MS_PER_DAY)
      : periodConfig.unit === "months"
        ? Math.round(
            (addMonths(dueDate, periodConfig.value).getTime() - dueDate.getTime()) /
              MS_PER_DAY,
          )
        : periodConfig.value;

    /*
     * periodLabel now shows the actual date range this
     * installment/interest period spans, e.g.:
     * Weekly: 16/07/2026-22/07/2026
     * Daily:  16/07/2026-16/07/2026 (unchanged from before)
     */
    const periodEndDate = nextDueDate
      ? new Date(nextDueDate.getTime() - MS_PER_DAY)
      : dueDate;

    const openingBalance = balance;
    const closingBalance = openingBalance + collectionAmount;

    /*
     * Balance × Rate / 100 / 365 × periodDays
     *
     * Example (weekly, periodDays = 7):
     * 1500 × 6% / 365 × 7
     * = 1.726...
     * = 1.73 (rounded up)
     */
    const rawInterest =
      (closingBalance * (annualInterestRate / 100) * periodDays) / 365;

    const interest = Math.ceil(rawInterest * 100) / 100;

    /*
     * Add daily interest to current compounding cycle.
     */
    accruedInterest += interest;

    /*
     * Track complete interest earned.
     */
    totalInterestEarned += interest;

    compoundInterest = 0;

    /*
     * Compounding trigger.
     *
     * Previously this checked for an EXACT date match, which
     * only worked when rows were generated daily. With
     * weekly/ten-daily/bi-weekly/monthly stepping, a due date
     * can easily jump PAST the compounding date without ever
     * landing on it exactly — so we now trigger as soon as the
     * due date reaches or passes it, and advance the compounding
     * date forward (possibly through multiple cycles, in case a
     * long collection period skips more than one compounding
     * boundary) until it's back in the future.
     */
    let shouldCompound = false;

    while (
      nextCompoundingDate &&
      dueDate.getTime() >= nextCompoundingDate.getTime()
    ) {
      shouldCompound = true;
      nextCompoundingDate = addMonths(nextCompoundingDate, compoundingMonths);
    }

    if (shouldCompound) {
      compoundInterest = accruedInterest;

      // Add accumulated interest to balance.
      balance = closingBalance + compoundInterest;

      // Reset interest for the next compounding cycle.
      accruedInterest = 0;
    } else {
      balance = closingBalance;
    }

    /*
     * Maturity Amount
     *
     * If interest was compounded today:
     * balance already contains the compounded interest.
     *
     * Otherwise:
     * balance + currently accrued interest.
     */
    const maturityAmount = balance + accruedInterest;

    schedule.push({
      emiNo: i + 1,

      periodLabel: `${formatDMY(dueDate)}-${formatDMY(periodEndDate)}`,

      dueDate: dueDate.toISOString(),

      nextSchedule,

      openingBalance,

      collectionAmount,

      Installment: collectionAmount,

      principal: collectionAmount,

      closingBalance,

      compoundInterest: Math.ceil(compoundInterest * 100) / 100,

      interest,

      maturityAmount: maturityAmount,

      collectionStatus: null,

      penaltyApplied: 0,

      paymentType,

      customerId,

      collectorId,

      accountId,

      companyId,

      collectedDate: null,
    });
  }

  const lastRow = schedule.at(-1);

  const principalAmount = collectionAmount * totalTenure;

  /*
   * Final maturity consists of:
   *
   * Principal
   * +
   * All compounded interest
   * +
   * Any interest remaining in the current cycle
   */
  const maturityAmount = lastRow.maturityAmount;

  return {
    schedule,

    summary: {
      principalAmount,

      interestEarned: totalInterestEarned,

      maturityAmount: maturityAmount,

      maturityDate: formatDMY(new Date(lastRow.dueDate)),

      interestCompounding,
    },
  };
}
